import { useMutation, useQueryClient } from '@tanstack/react-query';
import Config from 'src/common/Config';

const API_URL = Config.getApiUrl();

type SaveDocumentInput = {
  versionId: string;
  setAsPrimary: boolean;
};

type SaveDocumentResponse = {
  versionId: string;
  versionsCleanedUp: number;
};

export function useSaveDocument(documentId?: string) {
  const queryClient = useQueryClient();

  const matchesVersionsQuery = (query: { queryKey: readonly unknown[] }) =>
    query.queryKey.some(
      key => typeof key === 'string' && key.includes(`documents/${documentId}/versions`),
    );

  const {
    mutateAsync,
    error,
    isPending: isLoading,
    reset,
  } = useMutation({
    mutationFn: async (input: SaveDocumentInput): Promise<SaveDocumentResponse> => {
      if (!documentId) throw new Error('documentId is required');
      const response = await fetch(`${API_URL}/documents/${documentId}/finalise`, {
        method: 'POST',
        body: JSON.stringify({ versionId: input.versionId, setAsPrimary: input.setAsPrimary }),
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      if (response.status < 200 || response.status > 299) throw response;
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ predicate: matchesVersionsQuery });
    },
  });

  return {
    saveDocument: mutateAsync,
    error,
    isLoading,
    reset,
  };
}
